"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const Vue = require("vue");
const Client_1 = require("../../api/Client");
const DevtoolsMessenger_1 = require("../../lib/devtools/DevtoolsMessenger");
exports.default = Vue.defineComponent({
    name: 'HeroScriptPanel',
    components: {},
    setup() {
        return {
            commandIdsByLineKey: {},
            lineElemsByIdx: Vue.reactive({}),
            focusedPositions: Vue.reactive([]),
            focusedCommandId: Vue.ref(null),
            scriptsByFilename: Vue.reactive({}),
            commandsById: Vue.reactive({}),
            scrollOnTimeout: -1,
        };
    },
    watch: {
        ['focusedPositions.0.line'](value) {
            const firstLine = value;
            if (!firstLine)
                return;
            clearTimeout(this.scrollOnTimeout);
            this.scrollOnTimeout = setTimeout(() => {
                const $el = this.lineElemsByIdx[firstLine];
                if ($el)
                    $el.scrollIntoView({ block: 'center' });
            });
        },
    },
    computed: {
        activeFileLines() {
            const filename = this.activeFilename;
            if (!filename)
                return [];
            return this.scriptsByFilename[filename] ?? [];
        },
        activeFilename() {
            return this.focusedPositions?.length ? this.focusedPositions[0].filename : null;
        },
    },
    methods: {
        getCallsForLine(line) {
            const filename = this.activeFilename;
            if (!filename)
                return [];
            const commands = this.getCommandsAtPosition(line, filename);
            if (!commands?.length)
                return [];
            return commands.map((x, i) => {
                return {
                    commandId: x.command.id,
                    callInfo: `${i + 1} of ${commands.length}`,
                    active: this.focusedCommandId === x.command.id,
                };
            });
        },
        onSelectClick(e) {
            e.stopPropagation();
        },
        changedFocusedCommand() {
            Client_1.default.send('Session.timetravel', {
                heroSessionId: null,
                commandId: this.focusedCommandId,
            }).catch(err => alert(err.message));
        },
        clickLine(line, filename) {
            const commandIds = this.commandIdsByLineKey[`${filename}_${line}`];
            if (!commandIds?.size)
                return;
            Client_1.default.send('Session.timetravel', {
                heroSessionId: null,
                commandId: [...commandIds][0],
            }).catch(err => alert(err.message));
        },
        getCommandsAtPosition(line, filename) {
            if (!filename || line === undefined || line === null)
                return [];
            const commandIds = this.commandIdsByLineKey[`${filename}_${line}`];
            if (commandIds) {
                return [...commandIds].map(id => this.commandsById[id]).filter(Boolean);
            }
            return [];
        },
        getClassesForLineIndex(index) {
            const filename = this.activeFilename;
            const active = this.focusedPositions?.some(x => x.line === index + 1 && x.filename === filename) ?? false;
            const commandsAtLine = this.getCommandsAtPosition(index + 1, this.activeFilename);
            const hasError = commandsAtLine.some(x => x.command.resultType?.includes('Error'));
            return {
                active,
                error: hasError,
                'multi-call': commandsAtLine.length > 1,
                command: commandsAtLine.length > 0,
            };
        },
        onCommandUpdated(message) {
            this.commandsById[message.command.id] = message;
            for (const position of message.originalSourcePosition ?? []) {
                const key = `${position.filename}_${position.line}`;
                this.commandIdsByLineKey[key] ??= new Set();
                this.commandIdsByLineKey[key].add(message.command.id);
            }
            if (!this.focusedCommandId && message.originalSourcePosition?.length) {
                this.setFocusedPositions(message.originalSourcePosition);
            }
        },
        onCommandFocused(message) {
            this.focusedCommandId = message.commandId;
            const position = this.commandsById[message.commandId]?.originalSourcePosition;
            if (!position?.length)
                return;
            this.setFocusedPositions(position);
        },
        onSourceCodeUpdated(message) {
            this.scriptsByFilename[message.filename] = message.lines;
            if (!this.focusedPositions.length) {
                this.focusedPositions.push({ filename: message.filename, line: 1 });
            }
        },
        onScriptStateResponse(message) {
            if (!message)
                return;
            for (const ev of Object.values(message.commandsById)) {
                this.onCommandUpdated(ev);
            }
            for (const [filename, lines] of Object.entries(message.sourceFileLines)) {
                this.onSourceCodeUpdated({ filename, lines });
            }
        },
        // private functions
        setFocusedPositions(positions) {
            positions ??= [];
            this.focusedPositions.length = positions.length;
            Object.assign(this.focusedPositions, positions);
        },
    },
    async mounted() {
        (0, DevtoolsMessenger_1.sendToBackgroundScript)({ action: 'getMinerAddress' }, address => {
            window.setMinerAddress(address);
            Client_1.default.send('Session.getScriptState')
                .then(this.onScriptStateResponse)
                .catch(err => alert(String(err)));
            Client_1.default.on('SourceCode.updated', this.onSourceCodeUpdated);
            Client_1.default.on('Command.updated', this.onCommandUpdated);
            Client_1.default.on('Command.focused', this.onCommandFocused);
        });
    },
    beforeUnmount() {
        Client_1.default.off('SourceCode.updated', this.onSourceCodeUpdatd);
        Client_1.default.off('Command.updated', this.onCommandUpdated);
        Client_1.default.off('Command.focused', this.onCommandFocused);
    },
});
